import React, {
	Fragment,
	memo,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { PositionType } from '@atlaskit/tooltip/types';
import { JiraSprintAri } from '@atlassian/ari/jira/sprint';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionRendererFor,
	ActionStatusProps,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { classifySourceUrl } from '@atlassian/conversation-assistant-utils/classifySourceUrl';

import type { UpdateWorkItemAllFieldTypesAction } from '../../common/types';

import type { mainUpdateWorkItemAllFieldTypesActionQuery } from './__generated__/mainUpdateWorkItemAllFieldTypesActionQuery.graphql';
import { ActionCard } from './action-card';
import { CardGroupContext } from './CardGroupContext';
import { CardGroupFooter } from './CardGroupFooter';
import { messages } from './messages';
import { SystemMessage } from './SystemMessage';
import { groupUrlsByFrequency } from './url-grouping';
import {
	actionToIssueAri,
	areBulkActionButtonsDisabled,
	buildJqlForActions,
	cleanFieldsForBulkAction,
	createLookupKey,
	deduplicateActions,
	getActionData,
	getSingleAndBulkActions,
	getValidFieldIdsForAction,
	groupActionsByIssue,
	isUpdateWorkItemAllFieldsAction,
	validateJiraUpdateWorkAllFieldTypesAction,
} from './utils';

const styles = cssMap({
	cardGroup: {
		flexDirection: 'column',
		color: token('color.text'),
		backgroundColor: token('elevation.surface.raised'),
		transition: '200ms',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	cardsContainer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

export type JiraUpdateWorkItemAllFieldTypesActionRendererProps = ActionRendererFor<
	typeof ActionKeys.UpdateWorkItemAllFieldTypes
> & { showFooter?: boolean; showButtons?: boolean; tooltipPosition?: PositionType };

export const JiraUpdateWorkItemAllFieldTypesActionRenderer: React.MemoExoticComponent<
	({
		cloudId,
		actions,
		onCustomDataConfirm,
		onCancel,
		getStatus,
		getReadonly,
		jiraActivationId,
		showFooter,
		showButtons,
		...props
	}: JiraUpdateWorkItemAllFieldTypesActionRendererProps) => React.JSX.Element | null
> = memo(
	({
		cloudId,
		actions,
		messageId,
		onCustomDataConfirm,
		onCancel,
		getStatus,
		getReadonly,
		jiraActivationId,
		showFooter = true,
		showButtons = true,
		...props
	}: JiraUpdateWorkItemAllFieldTypesActionRendererProps): React.JSX.Element | null => {
		di(ActionCard, CardGroupFooter);

		const { sendAnalyticsEvent } = useAnalytics();
		const { formatMessage } = useIntl();

		const [isBulkActioning, setisBulkActioning] = useState(false);

		// Track which actions should be marked as finished or errored using lookup keys "fieldKey:issueId"
		const [bulkAcceptedActionKeys, setBulkAcceptedActionKeys] = useState<Set<string>>(new Set());
		const [bulkErroredActionKeys, setBulkErroredActionKeys] = useState<Set<string>>(new Set());
		const [invalidActionKeys, setInvalidActionKeys] = useState<Set<string>>(new Set());

		// Helper function get "effective" status of an single field action, which takes in consideration whether
		// a field has been accepted or errored by the bulk `update-all-field-types` action. This wraps getStatus to add
		// finished or errored keys tracking without mutating the `actions` array itself. This ensures we aren't
		// continually looping over and updating the `actions` array for each render of our component.
		const getEffectiveStatus = useCallback(
			(action: MessageAction): ReturnType<ActionStatusProps['getStatus']> => {
				if (isUpdateWorkItemAllFieldsAction(action)) {
					const { issueIdOrKey, fields } = action.data.updates?.[0] ?? {};
					// Single field actions only should be overriden in case they have been bulk actioned
					if (Object.keys(fields).length === 1) {
						const { fieldKey } = getActionData(action);
						const lookupKey = createLookupKey(fieldKey, issueIdOrKey);
						// If the action is marked as invalid, return its status as 'cancelled'
						if (invalidActionKeys.has(lookupKey)) {
							return 'cancelled';
						}
						// If the action is marked as being successfully bulk accepted, return its status as 'finished'
						if (bulkAcceptedActionKeys.has(lookupKey)) {
							return 'finished';
						}
						// If the action is marked as being bulk errored, return its status as 'errored'
						if (bulkErroredActionKeys.has(lookupKey)) {
							return 'errored';
						}
					}
				}

				return getStatus(action);
			},
			[bulkAcceptedActionKeys, bulkErroredActionKeys, getStatus, invalidActionKeys],
		);

		// We add invalid actions to the invalidActionKeys set so that we can return a "cancelled" status for the action so it does not render empty fields.
		// This is only passed in for fields which require hydration, as it is these fields (when invalid values are provided by the LLM) which mainl
		// cause empty fields to be rendered. For all other fields which rely on the Relay query, even if the value is invalid, they render successfully
		// but just cannot be executed successfully.
		const onInvalidAction = useCallback(
			(action: UpdateWorkItemAllFieldTypesAction) => {
				const { issueIdOrKey } = action.data.updates?.[0] ?? {};
				const fieldKey = getActionData(action).fieldKey;
				const lookupKey = createLookupKey(fieldKey, issueIdOrKey);

				// Track the invalid action keys to be used for analytics + rendering the correct system message
				setInvalidActionKeys((prev) => {
					const updated = new Set(prev);
					updated.add(lookupKey);
					return updated;
				});
			},
			[setInvalidActionKeys],
		);

		const validActions = useMemo(() => {
			const filteredActions = actions.filter(
				(action) => validateJiraUpdateWorkAllFieldTypesAction(action).isValid,
			);
			return deduplicateActions(filteredActions);
		}, [actions]);

		const numInvalidActions = useMemo(() => {
			if (!fg('jira-track-invalid-update-workitem-actions')) {
				return 0;
			}
			return actions.filter((action) => !validateJiraUpdateWorkAllFieldTypesAction(action).isValid)
				.length;
		}, [actions]);

		useEffect(() => {
			if (numInvalidActions === 0) {
				return;
			}

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'detected',
				actionSubject: 'invalidActions',
				actionSubjectId: 'updateWorkActionRenderer',
				attributes: { numActions: actions.length, numInvalidActions, messageId },
			});
		}, [actions.length, numInvalidActions, messageId, sendAnalyticsEvent]);

		// An "active action" is one that is not cancelled (so idle, accepted, or failed). We filter out cancelled actions so they are removed from view
		// as per product requirements. If an action card has no active actions, it will be unmounted.
		const validNonCancelledActions = useMemo(
			() =>
				validActions.filter(
					(action) =>
						!(action.status === 'cancelled' || getEffectiveStatus(action) === 'cancelled') &&
						!invalidActionKeys.has(
							createLookupKey(getActionData(action).fieldKey, getActionData(action).issueIdOrKey),
						),
				),
			[validActions, getEffectiveStatus, invalidActionKeys],
		);
		const cancelledActions = useMemo(
			() =>
				validActions.filter(
					(action) => action.status === 'cancelled' || getEffectiveStatus(action) === 'cancelled',
				),
			[validActions, getEffectiveStatus],
		);

		const { singleActions, bulkActions } = useMemo(
			() => getSingleAndBulkActions(validNonCancelledActions),
			[validNonCancelledActions],
		);

		// `allActionsAccepted` and `allActionsCancelled` are used to determine whether to render the system message for all actions accepted or cancelled.
		// Note that we consider the single actions only, as the bulk actions themselves are not rendered for the user. This does however consider the case
		// where a bulk action is accepted/cancelled, in which case the status of the single actions will be updated to calculate these two values.
		const allActionsAccepted = useMemo(
			() =>
				singleActions.every(
					(action) => action.status === 'finished' || getEffectiveStatus(action) === 'finished',
				),
			[singleActions, getEffectiveStatus],
		);
		const allActionsCancelled = useMemo(() => singleActions.length === 0, [singleActions]);

		// Using validActions instead of validNonCancelledActions as if validNonCancelledActions changes when a user dismisses all suggestions for an issue, we would
		// re-fetch the Relay query, and see an unexpected loading skeleton, which may be more expensive than fetching some unused data.
		const jqlString = useMemo(() => buildJqlForActions(validActions), [validActions]);

		// Retrieve a list of field keys to fetch in our relay query
		const fieldsToUpdate = useMemo(() => {
			const fieldKeysSet = new Set(
				validActions.map((action) => Object.keys(action.data.updates?.[0]?.fields ?? {})).flat(),
			);
			// As timespent is a custom field but we still need to fetch data from JiraTimeTrackingField, we manually add the
			// field id to the list of fields to fetch.
			if (
				validActions.some((action) => action.data.updates?.[0]?.fields?.hasOwnProperty('timespent'))
			) {
				// Swap out timespent for timetracking to fetch the correct data from JiraTimeTrackingField. timespent will map
				// to a JiraDurationField which is not relevant for this action, and will hence render a second unrelated field suggestion.
				fieldKeysSet.add('timetracking');
				fieldKeysSet.delete('timespent');
			}

			return Array.from(fieldKeysSet).sort();
		}, [validActions]);

		// A list of sprint ARIs to pass in to retrieve sprint names for the sprint field fragment.
		const sprintAris = useMemo(() => {
			const set = validActions.reduce<Set<string>>((acc, action) => {
				const { fieldValue } = getActionData(action);
				// Note that we are checking whether the field value itself is an integer instead of whether the field key
				// is "sprint", as sprint is a custom field and can have its key as `customfield_10001` etc.

				// Ideally, we could use the actionToFieldTypeMap to determine whether the field is a sprint, but it creates a circular
				// dependency as the actionToFieldTypeMap depends on the Relay query, but we need to pass the sprint ARIs into the query
				// as a variable.

				// For fields with values that are also integers (e.g story points), this means we create unused ARIs. As the ARI creation
				// is cheap and such dummy ARIs would not return a valid sprint in the Relay query, we can safely ignore these.
				const maybeSprintId = Number.isInteger(Number(fieldValue)) ? fieldValue?.toString() : null;
				if (!maybeSprintId || !jiraActivationId) {
					return acc;
				}
				acc.add(
					JiraSprintAri.create({
						siteId: cloudId,
						activationId: jiraActivationId,
						sprintId: maybeSprintId,
					}).toString(),
				);
				return acc;
			}, new Set());
			return Array.from(set);
		}, [validActions, cloudId, jiraActivationId]);

		const data = useLazyLoadQuery<mainUpdateWorkItemAllFieldTypesActionQuery>(
			graphql`
				query mainUpdateWorkItemAllFieldTypesActionQuery(
					$cloudId: ID!
					$jql: String!
					$fieldIdsOrAliases: [String!]!
					$sprintAris: [ID!]!
				) {
					jira {
						issueSearch(cloudId: $cloudId, issueSearchInput: { jql: $jql }, first: 50)
							@optIn(to: "JiraSpreadsheetComponent-M1") {
							edges {
								node {
									id
									key
									...actionCard_updateWorkItemAllFieldTypesActionRenderer
										@arguments(fieldIdsOrAliases: $fieldIdsOrAliases, sprintAris: $sprintAris)
									fieldsByIdOrAlias(idsOrAliases: $fieldIdsOrAliases, ignoreMissingFields: true)
										@optIn(to: "JiraIssueFieldsByIdOrAlias") {
										fieldId
										type
									}
								}
							}
						}
					}
				}
			`,
			{ cloudId, jql: jqlString, fieldIdsOrAliases: fieldsToUpdate, sprintAris },
		);

		const groupedActions = useMemo(
			() => groupActionsByIssue(singleActions, cloudId, data.jira?.issueSearch?.edges),
			[singleActions, cloudId, data.jira?.issueSearch?.edges],
		);

		// On each render, check all `update-all-field-types` actions to see if they have 'finished', 'errored' or 'cancelled'. If so,
		// we mark the corresponding single field actions as 'finished', 'errored' or 'cancelled' respectively.
		useEffect(() => {
			// Track single field actions that should be marked as finished or errored.
			const actionsToFinish = new Set<string>();
			const actionsToError = new Set<string>();

			for (const bulkAction of bulkActions) {
				const { issueIdOrKey } = bulkAction.data.updates?.[0] ?? {};
				// Get fieldKeys for the bulk action which are still planned and idle
				const fieldKeysForBulkAction = validActions
					.filter(
						(action) =>
							action.data.updates?.[0]?.issueIdOrKey === issueIdOrKey &&
							action.status === 'planned' &&
							getEffectiveStatus(action) === 'idle',
					)
					.map((action) => getActionData(action).fieldKey);

				// Retrieve field keys from the sanitized `update-all-field-types` action, and depending
				// on the status of the `update-all-field-types` action, we mark the corresponding single
				// field actions as 'finished' or 'errored' if necessary.
				fieldKeysForBulkAction.forEach((fieldKey) => {
					const lookupKey = createLookupKey(fieldKey, issueIdOrKey);
					if (bulkAction.status === 'finished' || getStatus(bulkAction) === 'finished') {
						actionsToFinish.add(lookupKey);
					} else if (bulkAction.status === 'errored' || getStatus(bulkAction) === 'errored') {
						actionsToError.add(lookupKey);
					}
				});
			}

			// Update state if there are new finished or erroredactions
			if (actionsToFinish.size > 0) {
				setBulkAcceptedActionKeys((prev) => {
					const updated = new Set(prev);
					actionsToFinish.forEach((key) => updated.add(key));
					return updated;
				});
			}

			if (actionsToError.size > 0) {
				setBulkErroredActionKeys((prev) => {
					const updated = new Set(prev);
					actionsToError.forEach((key) => updated.add(key));
					return updated;
				});
			}
		}, [
			validActions,
			bulkActions,
			setBulkAcceptedActionKeys,
			setBulkErroredActionKeys,
			getEffectiveStatus,
			getStatus,
		]);

		// Create a mapping from action to fieldType using the key format `issueId-fieldKey` to be used in analytics.
		const actionToFieldTypeMap = useMemo(() => {
			const map = new Map<string, Map<string, string>>();
			const issueSearch = data.jira?.issueSearch;

			if (!issueSearch?.edges) {
				return map;
			}

			validActions.forEach((action) => {
				const issueAri = actionToIssueAri(action, cloudId, data.jira?.issueSearch?.edges);
				if (!issueAri) {
					return;
				}
				if (!map.has(issueAri)) {
					map.set(issueAri, new Map<string, string>());
				}
				// Find matching edge with ARI
				const matchingEdge = issueSearch?.edges?.find((edge) => edge?.node?.id === issueAri);
				const fieldKeys = Object.keys(action.data.updates?.[0]?.fields ?? {});
				if (matchingEdge?.node?.fieldsByIdOrAlias) {
					fieldKeys.forEach((fieldKey) => {
						const matchingField = matchingEdge?.node?.fieldsByIdOrAlias?.find(
							(field) => field?.fieldId === fieldKey,
						);
						if (matchingField?.type) {
							map.get(issueAri)?.set(fieldKey, matchingField.type);
						}
					});
				}
			});
			return map;
		}, [data.jira?.issueSearch, validActions, cloudId]);

		// Helper function used to get the field type for each action - used for sending meaningful analytics events.
		// Only for single field actions
		const getFieldTypeForFieldKey = useCallback(
			(action: UpdateWorkItemAllFieldTypesAction, fieldKey: string) => {
				const issueAri = actionToIssueAri(action, cloudId, data.jira?.issueSearch?.edges);
				if (!issueAri) {
					return undefined;
				}
				return actionToFieldTypeMap.get(issueAri)?.get(fieldKey) ?? fieldKey;
			},
			[actionToFieldTypeMap, cloudId, data.jira?.issueSearch?.edges],
		);

		const getAllFieldTypesForAction = useCallback(
			(action: UpdateWorkItemAllFieldTypesAction) => {
				const issueAri = actionToIssueAri(action, cloudId, data.jira?.issueSearch?.edges);
				if (!issueAri) {
					return [];
				}
				const fieldKeys = Object.keys(action.data.updates?.[0]?.fields ?? {});
				return fieldKeys.map(
					(fieldKey) => actionToFieldTypeMap.get(issueAri)?.get(fieldKey) ?? fieldKey,
				);
			},
			[actionToFieldTypeMap, cloudId, data.jira?.issueSearch?.edges],
		);

		const onApplyAll = useCallback(async () => {
			setisBulkActioning(true);

			// Group by field type to be used for analytics
			const fields = bulkActions
				.filter((action) => action.status === 'planned' && getEffectiveStatus(action) === 'idle')
				.map((action) => getAllFieldTypesForAction(action))
				.flat();
			const fieldTypeCounts = fields.reduce(
				(acc, fieldType) => {
					acc[fieldType] = (acc[fieldType] || 0) + 1;
					return acc;
				},
				{} as Record<string, number>,
			);

			// Fire event for Apply all button being clicked
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'updateWorkApplyAll',
				attributes: {
					numFields: fields.length,
					fieldTypeCounts,
				},
			});

			// Fire confirm events for all idle actions and wait for them to complete
			const edges = data.jira?.issueSearch?.edges ?? [];
			await Promise.all(
				bulkActions
					.filter((action) => action.status === 'planned' && getEffectiveStatus(action) === 'idle')
					.map(async (action) => {
						const cleanedAction = cleanFieldsForBulkAction(
							action,
							validActions,
							getEffectiveStatus,
						);
						let fieldKeys: string[];
						let payloadToSend: Parameters<typeof onCustomDataConfirm>[1];

						const validFieldIds = getValidFieldIdsForAction(action, cloudId, edges);
						const cleanedFields = cleanedAction.data.updates?.[0]?.fields ?? {};
						const filteredFields = Object.fromEntries(
							Object.entries(cleanedFields).filter(([key]) => validFieldIds.has(key)),
						);
						fieldKeys = Object.keys(filteredFields);
						if (fieldKeys.length > 0) {
							const update = cleanedAction.data.updates[0];
							payloadToSend = {
								updates: [
									{
										issueIdOrKey: update?.issueIdOrKey ?? getActionData(action).issueIdOrKey,
										fields: filteredFields,
									},
								],
								...(action.data.sourceUrl && { sourceUrl: action.data.sourceUrl }),
							};
						} else {
							payloadToSend = undefined;
						}

						if (payloadToSend && fieldKeys.length > 0) {
							await onCustomDataConfirm(action, payloadToSend);
							for (const fieldKey of fieldKeys) {
								sendAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'updateWorkAccept',
									attributes: {
										fieldType: getFieldTypeForFieldKey(action, fieldKey),
										hasReasoning: !!action.data.updates?.[0]?.fieldReasoning?.[fieldKey],
										hasSourceUrl: !!action.data?.sourceUrl,
										...(fg('add-source-in-update-workitem-analytics')
											? { sourceUrlType: classifySourceUrl(action.data?.sourceUrl) }
											: {}),
										bulkActioned: true,
									},
								});
							}
						}
					}),
			);

			setisBulkActioning(false);
		}, [
			bulkActions,
			validActions,
			onCustomDataConfirm,
			sendAnalyticsEvent,
			getFieldTypeForFieldKey,
			getAllFieldTypesForAction,
			getEffectiveStatus,
			cloudId,
			data?.jira?.issueSearch?.edges,
		]);

		const onDismissAll = useCallback(async () => {
			setisBulkActioning(true);

			// Group by field type to be used for analytics
			const fields = singleActions
				.filter((action) => action.status === 'planned' && getEffectiveStatus(action) === 'idle')
				.map((action) => getAllFieldTypesForAction(action))
				.flat();
			const fieldTypeCounts = fields.reduce(
				(acc, fieldType) => {
					acc[fieldType] = (acc[fieldType] || 0) + 1;
					return acc;
				},
				{} as Record<string, number>,
			);

			// Fire event for Dismiss all button being clicked
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'updateWorkDismissAll',
				attributes: {
					numFields: fields.length,
					fieldTypeCounts,
				},
			});

			// Fire cancel events for all idle actions
			await Promise.all(
				singleActions
					.filter((action) => action.status === 'planned' && getEffectiveStatus(action) === 'idle')
					.map(async (action) => {
						await onCancel(action);
						const { fieldKey } = getActionData(action);
						sendAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'updateWorkDismiss',
							attributes: {
								fieldType: getFieldTypeForFieldKey(action, fieldKey),
								hasReasoning: !!action.data.updates?.[0]?.fieldReasoning?.[fieldKey],
								hasSourceUrl: !!action.data?.sourceUrl,
								bulkActioned: true,
							},
						});
					}),
			);

			setisBulkActioning(false);
		}, [
			onCancel,
			sendAnalyticsEvent,
			getFieldTypeForFieldKey,
			getAllFieldTypesForAction,
			getEffectiveStatus,
			singleActions,
		]);

		const areActionButtonsDisabled = useMemo(
			() => areBulkActionButtonsDisabled(singleActions, getStatus, getReadonly),
			[singleActions, getStatus, getReadonly],
		);

		const actionCards = useMemo(() => {
			if (validNonCancelledActions.length === 0) {
				return null;
			}

			const issueSearch = data.jira?.issueSearch;
			if (!issueSearch) {
				return null;
			}

			// When FG is on: only show a card for an issue if at least one of its actions has a field returned by the API (fieldsByIdOrAlias).
			// Otherwise we would render an empty card (header with no field rows).
			return issueSearch?.edges?.reduce<ReactNode[]>((cards, edge) => {
				if (!edge?.node) {
					return cards;
				}
				const issueActions = groupedActions.get(edge.node.id);
				if (!issueActions || issueActions.length === 0) {
					return cards;
				}

				if (
					issueActions.every(
						(action) => action.status === 'cancelled' || getEffectiveStatus(action) === 'cancelled',
					)
				) {
					return cards;
				}

				const apiFieldIds = new Set(
					edge.node.fieldsByIdOrAlias
						?.map((f) => f?.fieldId)
						.filter((id): id is string => typeof id === 'string') ?? [],
				);
				apiFieldIds.add('comment');
				apiFieldIds.add('issuelinks');
				if (apiFieldIds.has('timetracking')) {
					apiFieldIds.add('timespent');
				}
				const hasAtLeastOneVisibleField = issueActions.some((action) => {
					const fieldKeys = Object.keys(action.data.updates?.[0]?.fields ?? {});
					return fieldKeys.some((fk) => apiFieldIds.has(fk));
				});
				if (!hasAtLeastOneVisibleField) {
					return cards;
				}

				cards.push(
					<Fragment key={`${edge.node.id}-${edge.node.key}`}>
						<ActionCard
							cloudId={cloudId}
							queryRef={edge.node}
							actions={issueActions}
							onCustomDataConfirm={onCustomDataConfirm}
							onCancel={onCancel}
							getStatus={getEffectiveStatus}
							getReadonly={getReadonly}
							shouldExpand={cards.length === 0}
							isBulkActioning={isBulkActioning}
							showButtons={showButtons}
							onInvalidAction={onInvalidAction}
							{...props}
						/>
					</Fragment>,
				);
				return cards;
			}, []);
		}, [
			data.jira?.issueSearch,
			validNonCancelledActions,
			cloudId,
			onCustomDataConfirm,
			onCancel,
			getEffectiveStatus,
			getReadonly,
			props,
			isBulkActioning,
			groupedActions,
			onInvalidAction,
			showButtons,
		]);

		// Use validActions instead of validNonCancelledActions as a cancelled action may contain a sourceUrl which provides context for the suggestions.
		const canonicalSourceUrlBase = useMemo(() => {
			const urls =
				validActions
					?.map((action) => getActionData(action).sourceUrl)
					?.filter((url): url is string => typeof url === 'string' && url.length > 0) ?? [];
			if (urls.length === 0) {
				return undefined;
			}

			const groupedUrlsByFrequency = groupUrlsByFrequency(urls, { ignoreInvalid: true });

			// Return the most frequent URL's base (origin + pathname)
			return groupedUrlsByFrequency?.[0]?.base;
		}, [validActions]);

		useEffect(() => {
			if (actionCards && actionCards.length > 0) {
				const fields = validActions
					.filter((action) => action.status === 'planned' && getEffectiveStatus(action) === 'idle')
					.filter((action) => Object.keys(action.data.updates?.[0]?.fields ?? {}).length === 1)
					.map((action) => getAllFieldTypesForAction(action))
					.flat();

				// Group by field type to be used for analytics
				const fieldTypeCounts = fields.reduce(
					(acc, fieldType) => {
						acc[fieldType] = (acc[fieldType] || 0) + 1;
						return acc;
					},
					{} as Record<string, number>,
				);

				const sourceUrlAttributes = fg('add-source-in-update-workitem-analytics')
					? {
							hasSourceUrl: canonicalSourceUrlBase !== undefined,
							sourceUrlType: classifySourceUrl(canonicalSourceUrlBase),
						}
					: {};

				sendAnalyticsEvent({
					action: 'rendered',
					actionSubject: 'component',
					actionSubjectId: 'updateWorkActionRenderer',
					attributes: {
						numActionCards: actionCards.length,
						numFields: fields.length,
						fieldTypeCounts,
						...sourceUrlAttributes,
					},
				});
			}
			// Empty dependency array to ensure that we only fire the analytics event once when the component renders and there are action cards.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		// This single field action counts includes cancelled actions (whereas the singleActions variable does not). This helps
		// to deduce whether we are cancelling because of invalid fields or because a user actually dismissed an issue.
		const singleFieldActionCount = actions.filter(
			(action) => Object.keys(action.data.updates?.[0]?.fields ?? {}).length === 1,
		).length;

		// When all valid actions are cancelled, render the system message
		if (
			cancelledActions.length &&
			allActionsCancelled &&
			invalidActionKeys.size !== singleFieldActionCount
		) {
			return <SystemMessage message={formatMessage(messages.allActionsDismissed)} />;
		}

		// When no cards are shown (e.g. all filtered out due to no visible fields) but we have valid actions, show message
		if (actionCards?.length === 0 || !actionCards) {
			if (
				validNonCancelledActions.length > 0 ||
				invalidActionKeys.size === singleFieldActionCount
			) {
				return (
					<SystemMessage
						message={formatMessage(messages.noValidSuggestionsProvided)}
						variant="error"
					/>
				);
			}
			return null;
		}

		return (
			<Stack space="space.150">
				<CardGroupContext sourceUrl={canonicalSourceUrlBase} />
				<Flex
					xcss={styles.cardGroup}
					testId="update-work-item-all-fields-action-renderer.ui.action-card-group"
				>
					<Stack xcss={styles.cardsContainer}>{actionCards}</Stack>
					{showFooter && (
						<CardGroupFooter
							onDismiss={onDismissAll}
							onApplyUpdates={onApplyAll}
							isDisabled={areActionButtonsDisabled}
							allActionsApplied={allActionsAccepted}
							isBulkActioning={isBulkActioning}
						/>
					)}
				</Flex>
				{allActionsAccepted && (
					<SystemMessage message={formatMessage(messages.allActionsAccepted)} />
				)}
			</Stack>
		);
	},
);
